/**
 * 主站前台 - 文章详情页
 * 路由: /news/:slug
 * 数据源: GET /api/cms/articles/:slug
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../services/api';

const CATEGORY_LABELS = { news: '公司新闻', industry: '行业动态', features: '功能介绍' };

// 从JSONB content中提取HTML
const extractHtml = (content) => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (typeof content === 'object') {
    if (content.html) return content.html;
    // 尝试JSON字符串
    try {
      const parsed = typeof content === 'string' ? JSON.parse(content) : content;
      return parsed.html || '';
    } catch { return ''; }
  }
  return '';
};

export default function NewsDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get(`/cms/articles/${slug}`);
      setArticle(response.data.article);
    } catch (err) {
      if (err.response?.status === 404) {
        setError('文章不存在或尚未发布');
      } else {
        setError('加载失败，请稍后重试');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('zh-CN', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) {
    return (
      <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-gray-400 mt-4">加载中...</p>
        </div>
      </div>
    );
  }

  if (error || !article) {
    return (
      <div className="pt-24 pb-20 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-400 text-lg mb-6">{error || '文章不存在'}</p>
          <Link to="/news" className="px-6 py-2 gradient-bg text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity">
            返回新闻列表
          </Link>
        </div>
      </div>
    );
  }

  const contentHtml = extractHtml(article.content);

  return (
    <div className="pt-24 pb-20 min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 返回链接 */}
        <Link to="/news" className="inline-flex items-center text-sm text-gray-400 hover:text-white transition-colors mb-8">
          <span className="mr-2">←</span> 返回新闻列表
        </Link>

        {/* 文章头部 */}
        <header className="mb-10">
          {/* 分类 + 日期 */}
          <div className="flex items-center gap-4 mb-4">
            {article.category && (
              <span className="px-3 py-1 text-xs font-medium rounded-full bg-blue-500/20 text-blue-300">
                {CATEGORY_LABELS[article.category] || article.category}
              </span>
            )}
            <span className="text-sm text-gray-500">
              {formatDate(article.published_at || article.created_at)}
            </span>
          </div>

          {/* 标题 */}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
            {article.title}
          </h1>

          {/* 摘要 */}
          {article.summary && (
            <p className="text-lg text-gray-400 leading-relaxed">
              {article.summary}
            </p>
          )}
        </header>

        {/* 封面图 */}
        {article.cover_image && (
          <div className="mb-10 rounded-xl overflow-hidden">
            <img
              src={article.cover_image}
              alt={article.title}
              className="w-full object-cover"
            />
          </div>
        )}

        {/* 文章正文 */}
        {contentHtml ? (
          <article
            className="prose prose-invert prose-lg max-w-none
              prose-headings:text-white prose-headings:font-bold
              prose-p:text-gray-300 prose-p:leading-relaxed
              prose-a:text-blue-400 prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white
              prose-blockquote:border-blue-500 prose-blockquote:text-gray-400
              prose-code:text-blue-300 prose-code:bg-white/5 prose-code:px-1 prose-code:py-0.5 prose-code:rounded
              prose-pre:bg-gray-800/50 prose-pre:border prose-pre:border-white/10
              prose-img:rounded-lg
              prose-table:border-collapse
              prose-th:bg-white/5 prose-th:border prose-th:border-white/10 prose-th:px-4 prose-th:py-2
              prose-td:border prose-td:border-white/10 prose-td:px-4 prose-td:py-2"
            dangerouslySetInnerHTML={{ __html: contentHtml }}
          />
        ) : (
          <div className="text-center py-10 text-gray-500">
            暂无正文内容
          </div>
        )}

        {/* 底部分隔 */}
        <div className="mt-16 pt-8 border-t border-white/10 text-center">
          <Link to="/news" className="px-6 py-2 bg-white/5 text-gray-300 rounded-lg text-sm hover:bg-white/10 hover:text-white transition-all">
            ← 查看更多文章
          </Link>
        </div>
      </div>
    </div>
  );
}
